import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { reportsAPI, savedReportsAPI, statementsAPI } from '../services/api';
import '../styles/Statements.css';
import ReportEditor from './report-editor/ReportEditor';
import '../styles/Reports.css';

/** Reports grouped by the pack that provides them; other data sources are grouped under their dataset. */
const groupReadyReports = (items) => {
  const groups = new Map();
  for (const r of items) {
    const name = r.packTitle || r.datasetTitle || r.datasetName;
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(r);
  }
  return [...groups].map(([name, reports]) => ({ name, reports }));
};

/** Saved reports with create / open / delete, the ready-made reports data sources provide, and financial statements. */
const ReportsPage = () => {
  const navigate = useNavigate();
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const companyId = currentCompany?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  // /reports?new=1 (from the dashboard editor's widget library) opens the builder straight away.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreating(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [statements, setStatements] = useState([]);
  const [ready, setReady] = useState([]);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      const [res, st, library] = await Promise.all([
        savedReportsAPI.list(companyId),
        statementsAPI.list(companyId).catch(() => null),
        reportsAPI.library(companyId).catch(() => null),
      ]);
      setReports(res?.success ? res.data : []);
      setStatements(st?.success ? st.data : []);
      setReady(library?.success ? (library.data?.reports || []) : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (report) => {
    const res = await savedReportsAPI.create(companyId, report);
    if (!res?.success) throw new Error(res?.error || 'Failed to save report');
    setCreating(false);
    navigate(`/reports/${res.data.id}`);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete report "${r.name}"? Widgets showing it will be removed from dashboards.`)) return;
    try {
      await savedReportsAPI.delete(r.id, companyId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete report');
    }
  };

  return (
    <div className="report-page">
      <div className="page-header">
        <div>
          <h2>Reports</h2>
          {currentCompany && <p className="page-subtitle">Saved reports for: <strong>{currentCompany.name}</strong></p>}
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={() => setCreating(true)} disabled={!currentCompany}>+ New report</button>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

      {loading ? <div className="loading-spinner" /> : reports.length === 0 ? (
        <div className="dataset-empty-state"><p>{ready.length > 0 ? 'No saved reports yet. Create one, or open a ready-made report below.' : 'No saved reports yet. Create one to get started.'}</p></div>
      ) : (
        <div className="datasets-table-container">
          <table className="datasets-table reports-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Dataset</th><th>Visibility</th><th>Owner</th><th>Updated</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong>{r.description && <div className="kpi-key">{r.description}</div>}</td>
                  <td>{r.display?.displayType || 'table'}{r.display?.compare ? ' · vs previous' : ''}</td>
                  <td><code>{r.datasetName || '—'}</code></td>
                  <td>{r.visibility === 'private' ? 'Only me' : 'Company'}</td>
                  <td>{r.ownerUsername || '—'}</td>
                  <td>{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}</td>
                  <td className="dataset-actions">
                    <button className="btn-secondary btn-small" onClick={() => navigate(`/reports/${r.id}`)}>Open</button>
                    {(isAdmin || r.ownerId === user?.id) && <button className="btn-danger btn-small" onClick={() => handleDelete(r)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && ready.length > 0 && (
        <div className="statements-list">
          <h3>Ready-made reports</h3>
          {groupReadyReports(ready).map((group) => (
            <section key={group.name} className="ready-report-group" aria-label={`${group.name} reports`}>
              <h4>{group.name}</h4>
              <div className="datasets-table-container">
                <table className="datasets-table reports-table">
                  <thead>
                    <tr><th>Report</th><th>Type</th><th>Dataset</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {group.reports.map((r) => (
                      <tr key={`${r.datasetName}-${r.key}`}>
                        <td><strong>{r.title}</strong></td>
                        <td>{r.displayType || 'table'}</td>
                        <td><code>{r.datasetName}</code></td>
                        <td className="dataset-actions">
                          <button className="btn-secondary btn-small" onClick={() => navigate(`/reports/ready/${encodeURIComponent(r.datasetName)}/${encodeURIComponent(r.key)}`)}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {statements.length > 0 && (
        <div className="statements-list">
          <h3>Financial statements</h3>
          <div className="datasets-table-container">
            <table className="datasets-table reports-table">
              <thead>
                <tr><th>Statement</th><th>From</th><th>Default period</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {statements.map((s) => (
                  <tr key={`${s.datasetName}-${s.key}`}>
                    <td><strong>{s.title}</strong>{s.description && <div className="kpi-key">{s.description}</div>}</td>
                    <td><code>{s.datasetName}</code>{s.optionalMissing.length > 0 && <div className="kpi-key">without {s.optionalMissing.join(', ')}</div>}</td>
                    <td>{s.defaultPeriod || 'current snapshot'}</td>
                    <td className="dataset-actions">
                      {s.missing.length > 0
                        ? <span className="kpi-key">needs {s.missing.join(', ')}</span>
                        : <button className="btn-secondary btn-small" onClick={() => navigate(`/statements/${encodeURIComponent(s.datasetName)}/${encodeURIComponent(s.key)}`)}>Open</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <ReportEditor onClose={() => setCreating(false)} onCreate={handleCreate} onUpdate={() => {}} />
      )}
    </div>
  );
};

export default ReportsPage;
